/**
 * Hooks for the platform-admin impersonation flow.
 *
 * The provisioner mints a short-lived JWT via the operations backend and
 * opens a new tab to `/auth/impersonate-handoff?token=<jwt>`. That route uses
 * `useEndImpersonation` to wind the session down.
 */
import { useCallback } from 'react';
import { useApiMutation } from './useApiMutation';
import { operationsClient } from '@/lib/api/client';
import { authEndpoints } from '@/lib/api/endpoints';
import { useAuthStore } from '@/stores/auth.store';

/**
 * POSTs to /auth/impersonate/end so the backend can revoke the session and
 * write the audit trail. Resolves the promise even if the call fails so the
 * caller can always finish wiping local state — a broken backend must not
 * trap the operator inside an impersonated session.
 */
export function useEndImpersonation() {
  return useApiMutation<void, void>({
    mutationFn: async () => {
      await operationsClient.post(authEndpoints.impersonationEnd(), {});
    },
    showErrorToast: false,
  });
}

export interface StopImpersonationOptions {
  /**
   * Where to send the operator after the session is wound down. Used only
   * when `window.close()` fails (no opener / popup blocker), since the tab
   * was opened from the provisioner and should normally just close.
   */
  fallbackUrl?: string;
}

/**
 * Returns a callback that ends impersonation in three stages:
 *   1. Best-effort POST to /auth/impersonate/end (await, but tolerate failure)
 *   2. Synchronously clear local auth state
 *   3. Close the tab, or redirect to login if the tab can't be closed
 *
 * Stage 2 always runs; stage 1 failure is logged but never blocks the wind-down.
 */
export function useStopImpersonation(options: StopImpersonationOptions = {}) {
  const endMutation = useEndImpersonation();
  const { fallbackUrl = '/login' } = options;

  return useCallback(async () => {
    try {
      await endMutation.mutateAsync();
    } catch (err) {
      // Surface to the console so support can correlate with backend logs,
      // but don't block the local wind-down — see comment above.
      console.warn('[impersonation] /auth/impersonate/end failed; wiping local state anyway', err);
    } finally {
      useAuthStore.getState().logout();
      // Try to close the tab the provisioner opened. Browsers refuse to close
      // tabs that weren't opened via window.open(), so we fall through to a
      // redirect for the (rare) cases the operator landed here some other way.
      try {
        window.close();
      } catch {
        // Ignore — fallthrough to redirect below.
      }
      // If window.close() succeeded the page is already gone; the redirect
      // below is harmless. If it didn't, this is the fallback.
      window.location.href = fallbackUrl;
    }
  }, [endMutation, fallbackUrl]);
}
